import React, { useEffect } from 'react';
import 'openapi-explorer';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';
import { UserInformationSystem } from './connect/uis';
import ConnectFAQ from './connect/ConnectFAQ';
import CodeLibraries from './connect/CodeLibraries';
import getComponentWithTheme from '../themeProvider';
import StaffImageModal from './components/react/StaffImageModal';

// import {reactEventListener} from "openapi-explorer/dist/lib/react";
require('sma.common/sass/connect.sass');

const codeBlockTheme = {
	...a11yLight,
	'hljs-string': { color: '#005C7AFF' },
	'hljs-symbol': { color: '#005C7AFF' },
	'hljs-bullet': { color: '#005C7AFF' },
	'hljs-addition': { color: '#005C7AFF' },
	'hljs-title': { color: '#023D72' },
	'hljs-section': { color: '#023D72' },
	'hljs-number': { color: '#804600' },
	'hljs-built_in': { color: '#804600' },
	'hljs-builtin-name': { color: '#804600' },
	'hljs-literal': { color: '#804600' },
	'hljs-type': { color: '#804600' },
	'hljs-params': { color: '#804600' },
	'hljs-meta': { color: '#804600' },
	'hljs-link': { color: '#804600' },
	'hljs-attribute': { color: '#804600' },
	hljs: {
		display: 'block',
		overflowX: 'auto',
		background: '#f9f9f9',
		color: '#545454',
		padding: '1em',
		maxWidth: '800px',
		borderRadius: '3px',
		border: '1px solid #d0d2d3',
	},
};

const Connect = (props) => {
	const [openModal, setOpenModal] = React.useState(false);
	useEffect(() => {
		const apiExplorer = document.getElementsByTagName('openapi-explorer')[0];
		const style = document.createElement('style');
		style.innerHTML = `
			.nav-bar-path.active {
				color: #ffffff !important;
				font-weight: 500;
			}
		`;
		apiExplorer.shadowRoot.appendChild(style);
	});

	const curlRefreshCall = `curl "https://example.smapply.org/api/o/token/" -d "grant_type=refresh_token\\
&client_id=<CLIENT_ID>\\
&client_secret=<CLIENT_SECRET>\\
&refresh_token=<REFRESH_TOKEN>"`;

	const refreshResponse = `{
\t"access_token": "<YOUR_NEW_ACCESS_TOKEN>",
\t"expires_in": 7200,
\t"refresh_token": "<YOUR_NEW_REFRESH_TOKEN>",
\t"scope": "admin",
\t"token_type": "Bearer"
}`;

	// eslint-disable-next-line max-len
	const curlRevokeAccessTokenCall = `curl "https://example.smapply.org/api/o/revoke_token/" -d "client_id=<CLIENT_ID>\\
&client_secret=<CLIENT_SECRET>\\
&token=<ACCESS_TOKEN>"`;
	// eslint-disable-next-line max-len
	const curlRevokeRefreshTokenCall = `curl "https://example.smapply.org/api/o/revoke_token/" -d "client_id=<CLIENT_ID>\\
&client_secret=<CLIENT_SECRET>\\
&token=<REFRESH_TOKEN>"`;

	useEffect(() => {
		// eslint-disable-next-line no-undef
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.addedNodes.length) {
					mutation.addedNodes.forEach(() => {
						const apiExplorer = document.querySelector('openapi-explorer');
						if (apiExplorer) {
							if (apiExplorer.shadowRoot) {
								const targetDiv = apiExplorer.shadowRoot.getElementById('link-cmp--schemas-team');
								if (targetDiv) {
									let clickCount = 0;
									let clickTimeout;

									const handleClick = () => {
										clickCount += 1;

										if (clickTimeout) {
											clearTimeout(clickTimeout);
										}

										clickTimeout = setTimeout(() => {
											clickCount = 0;
										}, 2000);

										if (clickCount === 5) {
											setOpenModal(true);
											clickCount = 0;
										}
									};

									targetDiv.addEventListener('click', handleClick);
									observer.disconnect();
								}
							}
						}
					});
				}
			});
		});

		setTimeout(() => {
			const targetNode = document.body;
			if (targetNode) {
				observer.observe(targetNode, { childList: true, subtree: true });

				// Manual trigger to simulate node addition, couldn't trigger the observer
				// with the actual node addition?
				const newNode = document.createElement('div');
				newNode.textContent = 'New Node';
				document.body.appendChild(newNode);
			}
		}, 1000);

		return () => {
			observer.disconnect();
		};
	}, []);

	return (
		getComponentWithTheme(
			<>
				<Box className='container-fluid sma-connect-docs'>
					<openapi-explorer
						spec-url='/api/openapi.yaml'
						nav-bg-color='#05467e'
						nav-text-color='#dddddd'
						nav-hover-text-color='#ffffff'
						secondary-color='#ADD0EB'
						hide-server-selection
						hide-console={props.hideConsole}
						use-path-in-nav-bar={false}
					>
						<Box slot='overview'>
							<Typography variant='sectionTitle' component='h1' weight='medium'>
								Welcome to SurveyMonkey Apply Connect documentation!
							</Typography>
							<Typography component='p'>
								We&#39;re excited that you&#39;re looking to start using Apply
								Connect! The
								documentation provides
								sample requests and responses for all the methods that are currently
								supported. Before you get
								started, here are some quick tips:
							</Typography>
							<Box my={5}>
								<dl>
									<dt>
										<Typography
											variant='cardTitle'
											component='h4'
											weight='medium'
										>
											Authentication
										</Typography>
									</dt>
									<dd>
										All requests sent to the API must be authenticated with
										OAuth2.
										For more information,
										refer to
										{' '}
										<a href='#authentication?route=auth'>Authentication</a>
										.
									</dd>
									<dt>
										<Typography
											variant='cardTitle'
											component='h4'
											weight='medium'
										>
											Data
											Format
										</Typography>
									</dt>
									<dd>
										All data will be returned to you in JSON format, and all
										data,
										with the exception of file
										uploads, is sent as JSON.
									</dd>
									<dt>
										<Typography
											variant='cardTitle'
											component='h4'
											weight='medium'
										>
											Pagination
										</Typography>
									</dt>
									<dd>
										To avoid returning too many results at once, data returned
										through the API is paginated.
										Each response from a &#34;list&#34;-type endpoint (such as
										when
										you
										retrieve a list of users and not
										a particular user) has a next and a previous key. The values
										associated with these are URLs
										that point to the next/previous page of results. By default,
										we
										return a maximum of 25
										results per request. If you would prefer to receive a
										different
										number of results per page,
										the GET parameter
										{' '}
										<i>per_page</i>
										{' '}
										can be passed along. For
										example,
										<br />
										<br />
										<Box my={1} ml={5} mr={10} pr={10}>
											<SyntaxHighlighter
												language='http'
												style={codeBlockTheme}
											>
												GET
												https://example.smapply.org/api/programs/?per_page=20
											</SyntaxHighlighter>
										</Box>
									</dd>
								</dl>
							</Box>
						</Box>
						<Box slot='authentication'>
							<Typography
								variant='sectionTitle'
								component='h1'
								weight='medium'
							>
								Authentication
							</Typography>
							<Typography component='p'>
								Our API uses OAuth2 to authenticate your application. If you’re new
								to
								OAuth2, you can read about
								it in the
								{' '}
								<a href='https://tools.ietf.org/html/rfc6749'>
									<strong>
										official
										spec
									</strong>
								</a>
								.
							</Typography>

							<Typography component='p'>
								We’ve simplified the process so that the initial authentication is
								done
								through Apply from the
								administrator portal, making our authentication flow as follows:
							</Typography>
							<Box my={5}>
								<Box>
									<Typography variant='cardTitle' component='h4' weight='medium'>
										Generate your tokens and client credentials on our website
									</Typography>
								</Box>
								<Box>
									<Box mb={5}>
										<Typography component='p'>
											Log in to your site, go to the
											{' '}
											<a
												href='/admin/settings/integrations/'
											>
												<strong>
													Integrations
													Hub
												</strong>
											</a>
											{' '}
											and click on the Configure button for Apply
											Connect. If you do not already
											have credentials, you can click the
											{' '}
											<strong>
												Generate
												Credentials
											</strong>
											{' '}
											button to generate
											new ones. Once generated you will have an access token,
											a
											refresh token, a client ID,
											and a client secret. The client secret is only shown to
											you
											once (when the credentials are
											first generated), so copy it somewhere safe for future
											use.
											If you lose or forget your client
											secret you&#39;ll need to generate new credentials in
											order
											to
											use the API after your access
											token expires, so try not to lose it.
										</Typography>
									</Box>
									<Box my={5}>
										<Typography component='p'>
											These credentials are tied uniquely to your user account
											and
											to your site, anyone who has them
											can authenticate themselves as you through the API – so
											make
											sure you keep them secret. If at
											any time you feel that your API credentials may have
											been
											compromised, use the
											{' '}
											<strong>
												Revoke
												Credentials
											</strong>
											{' '}
											button to revoke your current
											credentials, which will invalidate and
											immediately erase the existing ones. You can then
											generate
											new ones if needed. Alternatively,
											if you just need to invalidate your access or refresh
											tokens, you can click on the
											{' '}
											<strong>
												Refresh Credentials
											</strong>
											{' '}
											button to get a new set of
											tokens without changing your client id
											or secret.
										</Typography>
									</Box>
									<Box>
										<Typography weight='medium'>
											Make sure that you update any applications that depend
											on
											these credentials if you do make a
											change, otherwise they will no longer be able to
											communicate
											with our API.
										</Typography>
									</Box>
								</Box>
							</Box>
							<Box my={5}>
								<Box>
									<Typography variant='cardTitle' component='h4' weight='medium'>
										Make your request, receive your data
									</Typography>
								</Box>
								<Box>
									<Typography component='p'>
										For your request to be authenticated, the access token you
										received from the previous step needs
										to be added to the header of every request that you send us.
										For
										example:
									</Typography>
									<Box my={5} ml={5} mr={10} pr={10}>
										<SyntaxHighlighter language='bash' style={codeBlockTheme}>
											curl &#34;https://example.smapply.org/api/programs/&#34; -H
											&#34;Authorization: Bearer smapply_access_token&#34;
										</SyntaxHighlighter>
									</Box>
									<Typography component='p'>
										If you receive a
										{' '}
										<b>403</b>
										{' '}
										error after sending a request, it&#39;s
										possible that your access token
										is invalid. You can check the API settings where you
										generated
										the token to make sure that it
										hasn&#39;t expired.
									</Typography>
								</Box>
							</Box>
							<Box my={5}>
								<Typography variant='cardTitle' component='h4' weight='medium'>
									When your access token has expired, refresh your tokens
								</Typography>
								<Typography component='p'>
									Access tokens expire 30 days after they are generated. When you
									make
									a call with an expired
									token you will get a
									{' '}
									<b>403</b>
									{' '}
									error. You need to refresh your
									access token to continue to make
									API calls.
								</Typography>
								<Box my={5} ml={5} mr={10} pr={10}>
									<SyntaxHighlighter language='bash' style={codeBlockTheme}>
										{curlRefreshCall}
									</SyntaxHighlighter>
								</Box>
								<Typography component='p'>
									Please note that
									the
									{' '}
									<i>grant_type</i>
									,
									{' '}
									<i>client_id</i>
									,
									{' '}
									<i>client_secret</i>
									{' '}
									and
									<i>refresh_token</i>
									{' '}
									arguments are not being sent in the url of the
									request. Those arguments
									should be sent as urlencoded data in the body of the POST
									request.
									If the call was successful,
									you will get a response of
								</Typography>
								<Box my={5} ml={5} mr={10} pr={10}>
									<SyntaxHighlighter language='json' style={codeBlockTheme}>
										{refreshResponse}
									</SyntaxHighlighter>
								</Box>
								<Typography component='p'>
									You can start using your new access token to authenticate
									requests
									until it expires. The
									new values can also be seen from your Account Settings page.
									Note
									that the client ID and client
									secret remain the same when you refresh your tokens.
								</Typography>
							</Box>
							<Box my={5}>
								<Typography variant='cardTitle' component='h4' weight='medium'>
									If you lose your credentials or they&#39;re compromised, revoke
									them
								</Typography>
								<Typography component='p'>
									Since access tokens are long lived (30 days), you may want to
									revoke
									it if you believe it
									has been unintentionally exposed or as a security measure if you
									think it won&#39;t be used
									again before it expires. Revoking an access token immediately
									stops
									the use of that token,
									but a new access token can be created programmatically using the
									refresh token at the
									<a href='#?route=post-/api/o/token/'>Refresh Token</a>
									{' '}
									endpoint if
									you need a new one.
									You can also revoke refresh tokens if you would like to stop new
									access tokens from being
									created programmatically, but keep in mind that revoking a
									refresh
									token also revokes
									the associated access token. You can use the
									<a
										href='#?route=post-/api/o/revoke_token'
									>
										Revoke Token
									</a>
									{' '}
									endpoint to revoke these tokens.
								</Typography>
								<Box my={5} ml={5} mr={10} pr={10}>
									<Typography component='p' weight='medium'>
										Revoke Access Token
									</Typography>
									<SyntaxHighlighter language='bash' style={codeBlockTheme}>
										{curlRevokeAccessTokenCall}
									</SyntaxHighlighter>
								</Box>
								<Box my={5} ml={5} mr={10} pr={10}>
									<Typography component='p' weight='medium'>
										Revoke Refresh Token
									</Typography>
									<SyntaxHighlighter language='bash' style={codeBlockTheme}>
										{curlRevokeRefreshTokenCall}
									</SyntaxHighlighter>
								</Box>
								<Typography component='p'>
									If you believe that your Client ID and Client Secret have been
									compromised, or you have
									forgotten or lost your Client Secret, you can use the Revoke
									Credentials button on the
									<a href='/admin/settings/integrations/apply_connect/'>
										<strong>
											Apply
											Connect
										</strong>
									</a>
									integrations page to revoke those credentials completely.
								</Typography>

							</Box>
						</Box>
						<Box slot='nav-section'>Code Libraries</Box>
						<Box slot='custom-section'>
							<CodeLibraries theme={codeBlockTheme} />
						</Box>
						<Box slot='nav-section'>User Information System</Box>
						<Box slot='custom-section'>
							<UserInformationSystem theme={codeBlockTheme} />
						</Box>
						<Box slot='nav-section'>FAQ</Box>
						<Box slot='custom-section'>
							<ConnectFAQ theme={codeBlockTheme} />
						</Box>
					</openapi-explorer>
				</Box>
				<StaffImageModal
					open={openModal}
					setOpen={setOpenModal}
				/>
			</>,
			(window.LOCALE) ? window.LOCALE.theme : 'default',
		)
	);
};

module.exports = Connect;
